import { isString } from "@sinch/feature-flags";
import { atom } from "jotai";
import type { DeepPartial } from "simplytyped";
import { v4 as uuidv4 } from "uuid";
import type { Accounting, AccountInfo } from "./api/engage/engage.types";
import type { ShellUser } from "./api/types";
import type {
  FeatureFlagUser,
  FeatureFlaggingContract,
  FeatureFlaggingDashboardContract,
  FeatureFlaggingMessageMediaContract,
} from "./featureFlagsUserContract";
import { safeRetrieveValue } from "./helpers/safeRetrieveValue";
import { getSessionStorage, setSessionStorage } from "./helpers/sessionStorage";

const na = "N/A";

export const getFeatureFlagsMMContract = (
  user: ShellUser,
  location: Window["location"],
  hiveBillingAccountId: string
): FeatureFlaggingMessageMediaContract => ({
  identifier: user.account?.id ?? getSessionId(),
  email: user?.email ?? na,
  country: user?.account?.country?.toLowerCase() ?? na,
  custom: {
    stableId: getStableId(),
    // Account based
    accountId: user.account?.id || "",
    emailDomain: user?.email
      ? user.email.slice(user.email.indexOf("@") + 1)
      : na,
    vendor: safeRetrieveValue(user.settings, "vendorId", isString) ?? na,
    paymentType:
      safeRetrieveValue(user.settings, "paymentType", isString) ?? na,
    language: user.language || na,
    userId: user.id ?? na,
    userAccountFlags: user.rights || [],
    // Environment based
    host: location.host,
    hostname: location.hostname,
    search: location.search,
    hiveBillingAccountId,
  },
});

/**
 * Converts input data into data matching our `FeatureFlaggingContract`. The
 * function returns a ConfigCat user object. The `FeatureFlaggingContract` type
 * should match `CCUser` and help us uphold the contract with the targeting
 * rules in ConfigCat.
 */
export const getFeatureFlagsUserContract = (
  user: ShellUser,
  accounting: DeepPartial<Accounting>,
  accountInfo: DeepPartial<AccountInfo>,
  location: Window["location"]
): FeatureFlaggingContract => ({
  identifier: (user.id ?? na).toString(),
  email: user.email ?? na,
  country: accounting.country?.toLowerCase() ?? na,
  custom: {
    // Account based
    accountId: String(accounting.account_id ?? ""),
    trialStart: accountInfo.testphase_start?.toString() || na,
    contractStart: accountInfo.contract_start?.toString() || na,

    // User based
    /** @deprecated Use projectId instead. */
    channelId: (typeof user.settings?.activeChannel === "number"
      ? user.settings.activeChannel
      : na
    ).toString(),
    projectId: (user.activeProject ?? na).toString(),
    language: user.language ?? na,
    emailDomain: user.email
      ? user.email.slice(user.email.indexOf("@") + 1)
      : na,
    // Environment based
    host: location.host,
    hostname: location.hostname,
    search: location.search,
  },
});

export const getFeatureFlagsDashboardContract = (
  user?: ShellUser | null
): FeatureFlaggingDashboardContract => {
  const accountType = safeRetrieveValue(
    user?.settings ?? {},
    "accountType",
    isString
  );

  return {
    identifier: getSessionId(),
    custom: {
      ...(user?.id && { userId: user.id }),
      ...(user?.account?.id && { accountId: user.account.id }),
      ...(accountType && { accountType }),
      ...(user?.activeProject && { projectId: user.activeProject.toString() }),
      ...(user?.email && {
        emailDomain: user.email.slice(user.email.indexOf("@") + 1),
      }),
      ...(user?.createdAt && { createdAt: new Date(user.createdAt) }),
    },
  };
};

const SESSION_ID = "SESSION_ID";

export const getSessionId = () => {
  const existingSession = getSessionStorage(
    SESSION_ID,
    (x): x is string | null => isString(x, SESSION_ID),
    null
  );

  if (!existingSession) {
    const newSession = uuidv4();
    setSessionStorage(SESSION_ID, newSession);
    return newSession;
  }

  return existingSession;
};

const getFeatureFlagSessionContract = () => ({
  identifier: getSessionId(),
  custom: {
    language: window.navigator.language,
    host: location.host,
    hostname: location.hostname,
    search: location.search,
  },
});

export const featureFlagContractAtom = atom<FeatureFlagUser>(
  getFeatureFlagSessionContract()
);

export const STABLE_ID_KEY = "configcat_stable_id";

const getStableId = () => {
  let id = localStorage.getItem(STABLE_ID_KEY);

  if (!id) {
    id = uuidv4();
    localStorage.setItem(STABLE_ID_KEY, id);
  }

  return id;
};
